/**
 * Client-side tracking: dataLayer (GA4/GTM) and Meta Pixel (fbq).
 * Only call from client components. Scripts load after cookie consent, so
 * pushing events is safe when these run.
 * Event names from Section 30.3 (GA4 Custom Event Taxonomy).
 */

import { resolveClientVisitBroker, visitBrokerGa4Fields } from '@/lib/analytics/visit-broker'
import { trackEventWithCAPI } from '@/lib/meta-pixel-helpers'
import { readSessionId } from '@/lib/analytics/visitor-session'
import { pushDataLayerEvent } from '@/lib/analytics/ga4-browser-events'
import { isVisitorIntentEvent, recordIntentEvent } from '@/lib/analytics/visitor-intent'
import { hasAnalyticsConsent } from '@/components/CookieConsentBanner'
import { CONSENT_COOKIE, gpcFromNavigator, marketingSharingAllowed } from '@/lib/identity/consent'
import {
  CONTEXTUAL_CONSENT_ASK_EVENT,
  isContextualConsentEvent,
} from '@/lib/identity/consent-prompt'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    fbq?: (...args: unknown[]) => void
  }
}

/** GA4 custom event names (Section 30.3). */
export type EventName =
  | 'generate_lead'
  | 'tour_requested'
  | 'schedule_tour_click'
  | 'schedule_showing_click'
  | 'ask_question_click'
  | 'contact_agent_click'
  | 'email_agent'
  | 'call_initiated'
  | 'text_initiated'
  | 'cma_downloaded'
  | 'cma_anchor_click'
  | 'valuation_requested'
  | 'place_value_answer'
  /**
   * A visitor submitted the ADDRESS step of a valuation form, before any
   * contact was asked. Site queue SITE-02: the accept test is the share of
   * address submits that end in a valuation request, and that ratio had no
   * denominator — only the successful submit fired an event, so a visitor who
   * typed their house and left was invisible. Params: form, surface.
   */
  | 'address_submit'
  | 'sign_up'
  | 'open_house_rsvp'
  | 'open_house_page_view'
  | 'view_listing'
  | 'save_listing'
  | 'like_listing'
  | 'share_listing'
  | 'compare_listing'
  | 'compare_add'
  | 'compare_remove'
  | 'compare_share'
  | 'compare_pdf_download'
  | 'share'
  | 'view_photo_gallery'
  | 'play_video'
  | 'view_similar_listings'
  | 'search'
  | 'save_search'
  | 'view_community'
  | 'view_city'
  | 'view_neighborhood'
  | 'view_blog_post'
  | 'view_market_report'
  | 'download_report'
  | 'scroll_depth'
  | 'section_view'
  | 'click_cta'
  | 'calculator_used'
  | 'calculator_interact'
  | 'map_interaction'
  | 'share_collection'
  | 'ai_compare_used'
  | 'return_visit'
  | 'exit_intent_shown'
  | 'homepage_view'
  | 'hero_search'
  | 'hero_impression'
  | 'hero_city_chip'
  | 'featured_impression'
  | 'view_featured_listings'
  | 'community_impression'
  | 'newsletter_signup'
  // 'community_view' / 'city_view' / 'neighborhood_view' removed 2026-07-16
  // (conversion-audit #18): duplicate names for the same facts as the
  // verb-first 'view_community' / 'view_city' / 'view_neighborhood' above —
  // the page trackers fired BOTH, double-counting every geo pageview in GA4.
  | 'community_cta_click'
  | 'city_cta_click'
  | 'broker_view'
  | 'contact_agent'
  | 'view_landing_page'
  | 'form_start'
  // Pulse feed events — locked 2026-05-22.
  // See marketing_brain_skills/pulse-feed/SKILL.md for the surface spec.
  | 'pulse_feed_entry'      // user clicked "Browse the feed" entry CTA
  | 'pulse_card_view'       // card became >55% visible in viewport
  | 'pulse_card_like'       // tap on heart or double-tap on card
  | 'pulse_card_share'      // tap on share icon
  | 'pulse_cta_click'       // tap on a card's primary CTA (schedule/read/etc.)
  | 'pulse_filter_change'   // user changed a feed filter (city, event type)
  // Nav telemetry — locked 2026-06-09. Top conversion surface; every open + click measured.
  | 'nav_interact'          // panel open/close or link click inside the mega-menu
  // Experience-archetype engagement (components/site/experience/useEngagementTracking.ts).
  | 'dwell'
  | 'module_interact'
  | 'sarah_teaser_shown'
  | 'sarah_teaser_clicked'
  | 'sarah_launcher_dismiss'
  | 'sarah_launcher_restore'
  | 'sarah_chat_start'

function pushDataLayer(obj: Record<string, unknown>) {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(obj)
}

/**
 * Fire a Google Ads conversion (when send_to env is set). Only call from client after consent.
 */
function fireGoogleAdsConversion(sendTo: string | undefined) {
  if (typeof window === 'undefined' || !sendTo?.trim() || !window.gtag) return
  const raw = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${CONSENT_COOKIE}=`))
    ?.split('=')[1]
  if (
    !marketingSharingAllowed({
      consentCookie: raw,
      gpc: gpcFromNavigator(typeof navigator !== 'undefined' ? navigator : undefined),
    })
  ) {
    return
  }
  window.gtag('event', 'conversion', { send_to: sendTo.trim() })
}

const GOOGLE_ADS_CONVERSION_SIGNUP = process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_SIGNUP?.trim()

/**
 * Push a typed event to window.dataLayer for GTM/GA4.
 *
 * No Google Ads lead conversion fires here. generate_lead is server-only
 * (lib/lead-tracking.ts) and a build test bans trackEvent('generate_lead')
 * (lib/analytics/lead-event.test.ts), so the old in-page AW lead branch could
 * never run; it was removed in audit 342. To count leads in Ads, import the
 * GA4 generate_lead event (docs/GOOGLE_ADS_SETUP.md); do not add an in-page
 * lead tag back (double count).
 *
 * GA4 gets the event from GTM's GA4 Event tag, never from a gtag('event') here
 * (lib/analytics/ga4-browser-events.ts). Since 1224b1f (2026-08-18) GTM's Google
 * tag is the only GA4 config, so a bare gtag('event') reached nothing; once the
 * GTM tag is published, a gtag copy would be the double count.
 */
export function trackEvent(eventName: EventName, params: Record<string, unknown> = {}) {
  noteVisitorIntentEvent(eventName)
  pushDataLayerEvent(eventName, params)
  if (typeof window !== 'undefined' && isContextualConsentEvent(eventName)) {
    window.dispatchEvent(new CustomEvent(CONTEXTUAL_CONSENT_ASK_EVENT))
  }
}

/** CMA section CTA: the visitor asked for the analysis. Key event. */
export function trackCmaAnchorClick(params: { listing_key: string; surface?: string; cta?: string }) {
  trackEvent('cma_anchor_click', params)
}

/** Published CMA download succeeded and the document URL is on the page. Key event. */
export function trackCmaDownloaded(params: { listing_key: string; surface?: string; form?: string; form_id?: string }) {
  trackEvent('cma_downloaded', params)
}

/** Tour form submit succeeded (not the click that opened it). Key event. */
export function trackTourRequested(params: {
  listing_key?: string
  form?: string
  form_id?: string
  lead_type?: string
}) {
  trackEvent('tour_requested', params)
}

/**
 * A buyer or seller event (lib/analytics/visitor-intent.ts BUYER_INTENT_EVENTS,
 * SELLER_INTENT_EVENTS) updates the browser's visitor-intent memory. When the
 * label changes, the new `visitor_intent` is pushed flat BEFORE the event itself,
 * so the GA4 hit for that event already carries the new user property (GTM
 * version 10 maps the flat key on both GA4 tags). Pushed after, the event that
 * made someone a seller would still say "browsing", and a visitor who leaves
 * right after it would never send the new label at all.
 *
 * Cheap for every other event: the name check runs first. Storage is written only
 * with analytics consent. Never throws.
 */
function noteVisitorIntentEvent(eventName: string): void {
  if (typeof window === 'undefined' || !isVisitorIntentEvent(eventName)) return
  try {
    const update = recordIntentEvent(eventName, { consent: hasAnalyticsConsent() })
    if (update.changed && update.visitor_intent) pushDataLayer({ visitor_intent: update.visitor_intent })
  } catch {
    /* tracking never breaks a page */
  }
}

/**
 * Stamp `assigned_broker` (USER) + `broker_slug` (EVENT) when ?agent= or the
 * attribution cookie names a broker. Same param names as fireLeadGenerated.
 * Returns the fields so callers can attach them to this page_view.
 */
export function applyVisitBrokerToGtag(): { broker_slug: string; assigned_broker: string } | null {
  const fields = visitBrokerGa4Fields(resolveClientVisitBroker())
  if (!fields) return null
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('set', 'user_properties', fields.userProperties)
  }
  return {
    broker_slug: fields.eventParams.broker_slug,
    assigned_broker: fields.userProperties.assigned_broker,
  }
}

/**
 * Push a client-side page view to window.dataLayer. When broker attribution is
 * known, the event carries broker_slug and the user property assigned_broker is
 * set — not only on generate_lead.
 *
 * GTM's "GA4 - page_view" tag (Custom Event `page_view`, GTM version 9,
 * 2026-10-14) sends this push to GA4 with page_type, broker_slug, page_location
 * and page_referrer. It is the only page_view sender: the Google tag has
 * send_page_view false and enhanced measurement's history page views are off.
 * It is not on the shared "GA4 - dataLayer events" trigger.
 */
export function trackPageView(pageType: string, params: Record<string, unknown> = {}) {
  const broker = applyVisitBrokerToGtag()
  const withBroker = broker ? { broker_slug: broker.broker_slug, ...params } : params
  // Undefined values are dropped: GTM's data model would store them and wipe a
  // sticky key such as broker_slug (same rule as pushDataLayerEvent).
  const flat: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(withBroker)) if (value !== undefined) flat[key] = value
  pushDataLayer({ event: 'page_view', page_type: pageType, ...flat })
}

// ----------------------------------------------------------------------------
// First-party session id — stitches anonymous browsing to the CRM person
// they become when they identify. The session id, its lifecycle and the
// rule that ends it (30 minutes idle, a new campaign) live in ONE module,
// lib/analytics/visitor-session.ts; the trackers advance it
// (components/VisitTracker.tsx firstPartyEventContext) and lead forms read it
// here, and the server replays prior events onto crm_people.
//
// This import is also part of WHEN that module is evaluated: this file is in
// the bundle every page hydrates with, so the session module records the page's
// arrival (its address, referrer and navigation type) at hydration, before the
// lazily loaded VisitTracker posts anything. A tap before it mounts no longer
// changes the address the arrival is judged on.
// ----------------------------------------------------------------------------

/**
 * The visitor session id as the trackers left it in localStorage. Returns
 * undefined when absent or malformed (so it drops cleanly from a submission
 * object). Never creates one and never advances the session clock — that is the
 * tracker's job, and a form-only session with no prior tracked events has
 * nothing to backfill anyway. Past 30 minutes of inactivity the id still names
 * the session that holds the browsing history, which is the one a form submit
 * must stitch; every other session on the browser is stitched by rr_vid.
 */
export function readRrSessionId(): string | undefined {
  return readSessionId() ?? undefined
}

// ----------------------------------------------------------------------------
// Landing page context: the page's own link. See
// marketing_brain_skills/tools_registry/ga4-instrumentation/SKILL.md for the LP
// tracking convention. view_landing_page fires once per landing-page view from
// components/tracking/LandingPageViewTracker.tsx, for every path
// lpVariantFromPath (lib/analytics/lp-variant.ts) names (Matt 2026-10-09:
// rebuild the GA4 landing-page audiences on /sell and /buy; GA4 property
// 527333348 held no view_landing_page from 2026-08-01 to 2026-10-09).
//
// The URL is the only source. Until 2026-10-14 trackLandingPageView also kept
// the context in sessionStorage (rr_lp_context) and getLpContext merged it back
// in, so a later form in the same TAB carried the old landing's utm_*. A tab
// lives for days on a phone, so a sign-up in a new, untagged visit was credited
// to an ad from days before. A sign-up is credited to the visit it happens in
// (Matt 2026-10-09): the server reads that visit's landing link when the page
// carries none (lib/crm/visit-attribution.ts).
// ----------------------------------------------------------------------------

export type LpContext = {
  lp_variant: string
  lp_source?: string
  lp_medium?: string
  lp_campaign?: string
  lp_content?: string
  lp_term?: string
  /** Meta click-id on this page's address. */
  fbclid?: string
  /** Google Ads click-id on this page's address. */
  gclid?: string
}

/**
 * The utm_* and click ids on the current page's address. Returns undefined keys
 * for missing params, never empty strings, so they drop cleanly from GA4 event
 * params and from a form's submission.
 */
export function getLpContext(lpVariant?: string): LpContext {
  if (typeof window === 'undefined') {
    return { lp_variant: lpVariant ?? 'unknown' }
  }
  const url = new URL(window.location.href)
  return {
    lp_variant: lpVariant ?? 'unknown',
    lp_source: url.searchParams.get('utm_source') || undefined,
    lp_medium: url.searchParams.get('utm_medium') || undefined,
    lp_campaign: url.searchParams.get('utm_campaign') || undefined,
    lp_content: url.searchParams.get('utm_content') || undefined,
    lp_term: url.searchParams.get('utm_term') || undefined,
    fbclid: url.searchParams.get('fbclid') || undefined,
    gclid: url.searchParams.get('gclid') || undefined,
  }
}

/**
 * Fire the canonical `view_landing_page` event for a landing-page view, with the
 * page's variant and its own utm_*. Returns the context it sent.
 */
export function trackLandingPageView(lpVariant: string): LpContext {
  const ctx = getLpContext(lpVariant)
  trackEvent('view_landing_page', {
    lp_variant: ctx.lp_variant,
    lp_source: ctx.lp_source,
    lp_medium: ctx.lp_medium,
    lp_campaign: ctx.lp_campaign,
    lp_content: ctx.lp_content,
    lp_term: ctx.lp_term,
  })
  return ctx
}

function trackFbq(event: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined' || !window.fbq) return
  if (params) {
    window.fbq('track', event, params)
  } else {
    window.fbq('track', event)
  }
}

/** Listing detail page view (view_item). */
export function trackListingView(params: {
  listingKey: string
  listingUrl: string
  price?: number
  city?: string
  state?: string
  mlsNumber?: string
  bedrooms?: number
  bathrooms?: number
}) {
  pushDataLayer({
    event: 'listing_view',
    listing_key: params.listingKey,
    listing_url: params.listingUrl,
    value: params.price,
    currency: 'USD',
    city: params.city,
    state: params.state,
    mls_number: params.mlsNumber,
    bedrooms: params.bedrooms,
    bathrooms: params.bathrooms,
  })
  pushDataLayer({
    event: 'view_item',
    ecommerce: {
      currency: 'USD',
      value: params.price ?? 0,
      items: [{
        item_id: params.listingKey,
        item_name: params.mlsNumber ?? params.listingKey,
        price: params.price,
        quantity: 1,
      }],
    },
  })
  const viewContent = {
    content_type: 'product',
    content_ids: [params.listingKey],
    content_name: params.mlsNumber ?? params.listingKey,
    value: params.price,
    currency: 'USD',
  }
  // Pixel + server CAPI with a shared event_id (dedup) so listing views survive
  // ad-blockers and feed value-based / catalog retargeting audiences.
  void trackEventWithCAPI('ViewContent', viewContent, { customData: viewContent })
}

/** Search / geo page view (search or view_search_results). */
export function trackSearchView(params: {
  searchTerm?: string
  city?: string
  subdivision?: string
  resultsCount?: number
}) {
  const searchTerm = [params.city, params.subdivision].filter(Boolean).join(' ')
  pushDataLayer({
    event: 'search_view',
    search_term: searchTerm || params.searchTerm,
    city: params.city,
    subdivision: params.subdivision,
    results_count: params.resultsCount,
  })
  pushDataLayer({
    event: 'view_search_results',
    search_term: searchTerm || params.searchTerm,
    results_count: params.resultsCount,
  })
  const searchContent = {
    search_string: searchTerm || params.searchTerm,
    content_category: 'real_estate',
  }
  void trackEventWithCAPI('Search', searchContent, { customData: searchContent })
}

/** Listing card/tile click (before navigation). */
export function trackListingClick(params: {
  listingKey: string
  listingUrl: string
  sourcePage: string
  price?: number
  city?: string
  mlsNumber?: string
}) {
  pushDataLayer({
    event: 'listing_click',
    listing_key: params.listingKey,
    listing_url: params.listingUrl,
    source_page: params.sourcePage,
    value: params.price,
    city: params.city,
    mls_number: params.mlsNumber,
  })
  trackFbq('ViewContent', {
    content_type: 'product',
    content_ids: [params.listingKey],
    content_name: params.mlsNumber ?? params.listingKey,
    value: params.price,
    currency: 'USD',
  })
}

/**
 * User saved a listing. This is mid-funnel buyer INTENT (AddToWishlist), NOT a
 * Lead. The old code fired both `fbq('Lead')` and GA4 `generate_lead` on every
 * save, so saves polluted the Lead conversion both Meta and GA4 optimize toward
 * — inflating lead volume and misdirecting ad budget toward savers. Saves are a
 * wishlist/retargeting signal and feed a dedicated audience, not the Lead count.
 */
export function trackSaveListing(params: {
  listingKey: string
  listingUrl: string
  price?: number
  mlsNumber?: string
}) {
  pushDataLayer({
    event: 'saved_property',
    listing_key: params.listingKey,
    listing_url: params.listingUrl,
    value: params.price,
    mls_number: params.mlsNumber,
  })
  pushDataLayer({
    event: 'add_to_wishlist',
    currency: 'USD',
    value: params.price,
    items: [{ item_id: params.listingKey, item_name: params.mlsNumber ?? params.listingKey }],
  })
  const wishlist = {
    content_type: 'product',
    content_ids: [params.listingKey],
    content_name: params.mlsNumber ?? params.listingKey,
    value: params.price,
    currency: 'USD',
  }
  void trackEventWithCAPI('AddToWishlist', wishlist, { customData: wishlist })
}

/** User signed up / created account. */
export function trackSignUp() {
  pushDataLayerEvent('sign_up', { method: 'Google' })
  void trackEventWithCAPI('CompleteRegistration', { content_name: 'Account created' }, { customData: { content_name: 'Account created' } })
  if (GOOGLE_ADS_CONVERSION_SIGNUP) fireGoogleAdsConversion(GOOGLE_ADS_CONVERSION_SIGNUP)
}
